import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { subscribeToDiscipline, subscribeToEvent, claimPoolSeat, lookupCode } from "../firebase/services";
import { getMatchedOfficialNames, getPoolAccessPoolId, getPoolAccessToken } from "../firebase/accessSessions";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";

export default function PoolJoin() {
  const { eventId, disciplineId, poolName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const decodedPoolName = decodeURIComponent(poolName);
  const { user } = useAuth();
  const [disc, setDisc] = useState(null);
  const [event, setEvent] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [autoJoinFailed, setAutoJoinFailed] = useState(false);
  const [accessReady, setAccessReady] = useState(() => (
    !!getPoolAccessToken(eventId, disciplineId, decodedPoolName)
    && !!getPoolAccessPoolId(eventId, disciplineId, decodedPoolName)
  ));
  const [accessError, setAccessError] = useState("");
  const autoJoinedRef = useRef(false);

  useEffect(() => {
    const unsub = subscribeToDiscipline(eventId, disciplineId, setDisc);
    return unsub;
  }, [eventId, disciplineId]);

  useEffect(() => {
    const unsub = subscribeToEvent(eventId, setEvent);
    return unsub;
  }, [eventId]);

  useEffect(() => {
    if (accessReady) return;
    const code = new URLSearchParams(location.search).get("code");
    if (!code) { setAccessError("Enter the pool code on the home screen to join."); return; }
    lookupCode(code).then((mapping) => {
      if (mapping?.type !== "pool" || mapping.eventId !== eventId || mapping.disciplineId !== disciplineId || mapping.poolName !== decodedPoolName) {
        throw new Error("This code does not match this pool");
      }
      setAccessReady(true);
    }).catch((err) => setAccessError(err.message || "Unable to verify pool code"));
  }, [accessReady, decodedPoolName, disciplineId, eventId, location.search]);

  const pool = disc?.pools?.find((p) => p.name === decodedPoolName);
  const accessPoolId = getPoolAccessPoolId(eventId, disciplineId, decodedPoolName);
  const poolAccessMatches = !!pool && !!accessPoolId && pool.id === accessPoolId;
  const judges = pool?.judges || [];
  const claimedJudges = pool?.claimedJudges || [];

  // Code redemption returns only roster names matching the signed-in user's
  // verified email; the complete official email map remains owner-only.
  const matchedOfficialNames = user ? getMatchedOfficialNames(eventId) : [];
  const matchedJudgeName = judges.find((name) =>
    !claimedJudges.includes(name) && matchedOfficialNames.includes(name)
  ) || null;
  const matchedRef = !!(pool?.ref && !pool.refClaimed && matchedOfficialNames.includes(pool.ref));

  const handlePickJudge = async (name) => {
    if (!accessReady || !poolAccessMatches || claiming || claimedJudges.includes(name)) return;
    setClaiming(true);
    try {
      await claimPoolSeat(eventId, disciplineId, pool.name, "judge", name);
      localStorage.setItem("judgeName", name);
      navigate(`/judge/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}`, { state: { judgeName: name } });
    } catch (err) {
      console.error(err);
      alert("Failed to join. Please try again.");
      setClaiming(false);
    }
  };

  const handlePickRef = async () => {
    if (!accessReady || !poolAccessMatches || claiming || pool.refClaimed) return;
    setClaiming(true);
    try {
      await claimPoolSeat(eventId, disciplineId, pool.name, "ref");
      navigate(`/ref/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}`);
    } catch (err) {
      console.error(err);
      alert("Failed to join. Please try again.");
      setClaiming(false);
    }
  };

  useEffect(() => {
    if (!pool || !accessReady || !poolAccessMatches || autoJoinedRef.current || claiming) return;
    if (!matchedJudgeName && !matchedRef) return;
    autoJoinedRef.current = true;
    (async () => {
      try {
        if (matchedJudgeName) {
          await claimPoolSeat(eventId, disciplineId, pool.name, "judge", matchedJudgeName);
          localStorage.setItem("judgeName", matchedJudgeName);
          navigate(`/judge/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}`, { state: { judgeName: matchedJudgeName } });
        } else {
          await claimPoolSeat(eventId, disciplineId, pool.name, "ref");
          navigate(`/ref/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}`);
        }
      } catch (err) {
        console.error(err);
        autoJoinedRef.current = false;
        setAutoJoinFailed(true);
      }
    })();
  }, [pool, poolAccessMatches, matchedJudgeName, matchedRef, claiming, accessReady, eventId, disciplineId, navigate]);

  if (!disc || !event) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;
  if (!pool) return <div className="flex items-center justify-center min-h-screen text-gray-600">Pool not found.</div>;
  if (accessReady && !poolAccessMatches) {
    return <div className="flex items-center justify-center min-h-screen p-6 text-center text-gray-500">This pool was reset. Enter the new pool code to continue.</div>;
  }
  if ((matchedJudgeName || matchedRef) && !autoJoinFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 text-sm">
        Signing you in as {matchedJudgeName || pool.ref}...
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6">
      <Link to="/" className="text-gray-600 text-sm mb-8 no-underline hover:text-gray-400">← Back</Link>
      <div className="text-center mb-8">
        <img src={logo} alt="" className="w-full max-w-xs mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2 mb-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[10px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">{pool.name}</h1>
        <p className="text-gray-500 text-sm mt-1">{disc.name}</p>
      </div>

      <div className="w-full max-w-xs">
        {accessError && <div role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2 mb-4">{accessError}</div>}
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">WHO ARE YOU?</div>
        <div className="flex flex-col gap-2">
          {pool.ref && (
            <button onClick={handlePickRef} disabled={!accessReady || !poolAccessMatches || pool.refClaimed || claiming}
              className="w-full p-3.5 rounded-xl text-left font-display text-base font-bold border cursor-pointer transition-all disabled:cursor-default"
              style={{
                background: pool.refClaimed ? "#111" : "#141414",
                borderColor: pool.refClaimed ? "#222" : "#333",
                color: pool.refClaimed ? "#555" : "#ccc",
                opacity: pool.refClaimed ? 0.5 : 1,
              }}>
              <div className="flex items-center justify-between">
                <span>{pool.ref}</span>
                <span className="text-xs" style={{ color: pool.refClaimed ? "#666" : "#bbb" }}>{pool.refClaimed ? "TAKEN" : "REF"}</span>
              </div>
            </button>
          )}
          {judges.map((name) => {
            const isTaken = claimedJudges.includes(name);
            return (
              <button key={name} onClick={() => handlePickJudge(name)} disabled={!accessReady || !poolAccessMatches || isTaken || claiming}
                className="w-full p-3.5 rounded-xl text-left font-display text-base font-bold border cursor-pointer transition-all disabled:cursor-default"
                style={{
                  background: isTaken ? "#111" : "#141414",
                  borderColor: isTaken ? "#222" : "#333",
                  color: isTaken ? "#555" : "#ccc",
                  opacity: isTaken ? 0.5 : 1,
                }}>
                <div className="flex items-center justify-between">
                  <span>{name}</span>
                  <span className="text-xs text-gray-600">{isTaken ? "TAKEN" : "JUDGE"}</span>
                </div>
              </button>
            );
          })}
        </div>
        {!pool.ref && judges.length === 0 && (
          <p className="text-gray-600 text-sm text-center py-8">No ref or judges assigned to this pool yet.</p>
        )}
      </div>
    </div>
  );
}
